import { useEffect, useState } from 'react';

import type { SavedCard } from '../api/types.ts';
import { useStore } from '../core/store.ts';
import { errorText, useStrings } from '../i18n/index.ts';
import { type CardInput, deleteCard, loadSavedCards, savedCardsStore, saveNewCard, setDefaultCard } from '../state/payment.ts';
import { sessionStore } from '../state/session.ts';
import { notify } from '../state/toasts.ts';
import { BankBadge, cardTitle } from '../ui/BankBadge.tsx';
import { Icon } from '../ui/icons.tsx';
import { CardForm } from '../ui/CardForm.tsx';
import { Button, Card, ConfirmDialog, cx, ScreenTitle } from '../ui/kit.tsx';
import { Screen } from '../ui/Screen.tsx';
import { Sheet } from '../ui/Sheet.tsx';

/**
 * Сохранённые карты: галочка у карты по умолчанию (тап по другой переносит её), «Изменить»
 * открывает удаление с подтверждением. «Добавить карту» открывает форму: номер уходит эквайеру
 * из браузера, банк ставит холд 1 RSD и снимает его, карта появляется в списке после возврата.
 */
export function CardsScreen() {
  const strings = useStrings();
  const cards = useStore(savedCardsStore);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState<SavedCard | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const session = useStore(sessionStore);

  useEffect(() => {
    void loadSavedCards(true);
  }, []);

  const makeDefault = async (card: SavedCard) => {
    if (card.isDefault || busy) return;
    try {
      await setDefaultCard(card.id);
    } catch (error) {
      notify(errorText(strings, error));
    }
  };

  const submitCard = async (card: CardInput) => {
    setBusy(true);
    try {
      await saveNewCard(card);
    } catch (error) {
      notify(errorText(strings, error));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await deleteCard(removing.id);
      notify(strings.cardDeleted);
      setRemoving(null);
    } catch (error) {
      notify(errorText(strings, error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Screen>
      <div className="readable stack">
        <div className="row cards-head">
          <ScreenTitle text={strings.paymentMethods} />
          {cards.items.length > 0 && (
            <button type="button" className="text-button cards-edit" onClick={() => setEditing((v) => !v)}>
              {editing ? strings.done : strings.edit}
            </button>
          )}
        </div>
        <div className="gutter stack cards-list">
          {cards.loaded && cards.items.length === 0 ? (
            <p className="t-subheadline t-muted cards-empty">{strings.noCardsHint}</p>
          ) : (
            <Card flush>
              {cards.items.map((card) => (
                <div key={card.id} className={cx('list-row card-row', editing && 'is-editing')}>
                  <BankBadge issuer={card.issuer} brand={card.brand} size={26} />
                  <button type="button" className="grow stack card-row-main" disabled={editing} onClick={() => void makeDefault(card)}>
                    <span className="profile-row-title">
                      {strings.paymentCardTitle} <span className="t-numeric">···· {card.last4 ?? '????'}</span>
                    </span>
                    <span className="t-footnote t-muted">
                      {cardTitle(card, card.brand ?? '')}
                      {card.expMonth && card.expYear ? ` · ${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}` : ''}
                    </span>
                  </button>
                  {editing ? (
                    <button type="button" className="icon-button card-row-delete" aria-label={strings.deleteCard} onClick={() => setRemoving(card)}>
                      <Icon name="trash" size={20} />
                    </button>
                  ) : (
                    card.isDefault && <Icon name="check" size={20} weight={2.6} className="card-row-check" />
                  )}
                </div>
              ))}
            </Card>
          )}
          {cards.items.length > 0 && <p className="t-footnote t-muted">{strings.defaultCardHint}</p>}
          {cards.loaded && !editing && (
            <Button disabled={busy} onClick={() => setAdding(true)}>
              {strings.addCard}
            </Button>
          )}
        </div>
      </div>
      {adding && (
        <Sheet size="auto" locked={busy} label={strings.newCard} onClose={() => { if (!busy) setAdding(false); }}>
          <div className="sheet-header">
            <button type="button" className="text-button" disabled={busy} onClick={() => setAdding(false)}>
              {strings.cancel}
            </button>
            <h2>{strings.newCard}</h2>
            <span />
          </div>
          <div className="sheet-body sheet-pad stack">
            <CardForm amountText="" submitTitle={strings.saveCard} note={strings.cardCheckHint} busy={busy} owner={session.profile?.name ?? ''} onSubmit={(card) => void submitCard(card)} />
          </div>
        </Sheet>
      )}
      {removing && (
        <ConfirmDialog
          title={strings.deleteCardTitle}
          text={strings.deleteCardText}
          confirmTitle={strings.deleteCard}
          destructive
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setRemoving(null)}
        />
      )}
    </Screen>
  );
}
